/**
 * コメント欄のテキストからURLを検出するユーティリティ。
 *
 * Notion 同期で本文中に取り込まれた埋め込みリンクのURLを、画面でクリック可能に
 * するために使う。同期側は Notion が返した href を加工せずそのまま保存するため、
 * ホスト名のない相対URL (例: /p/<page_id>) も本文に含まれる。相対URLは自ドメイン
 * への遷移になってしまうので、http/https のみをリンクとして扱う。
 */

// URLに使える文字は RFC 3986 の範囲（ASCII）に限られる。「参照(https://…)、以上」の
// ように日本語が続くケースでURLの一部として拾わないよう、文字クラスを明示する。
const URL_PATTERN = /https?:\/\/[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+/g;

// URLの直後に続く可能性がある区切り文字。これらは本文側の文字なのでURLに含めない。
const TRAILING_PUNCTUATION = '.,;:!?\'"';

/**
 * URLの末尾に紛れ込んだ区切り文字を落とす。
 *
 * 閉じ括弧は SharePoint の共有リンクのようにURL自体へ含まれることがあるため、
 * URL内で開き括弧が閉じ括弧より少ない場合だけ本文側の文字とみなして落とす。
 */
function trimTrailingPunctuation(url) {
	let trimmed = url;
	while (trimmed.length > 0) {
		const lastCharacter = trimmed[trimmed.length - 1];
		if (TRAILING_PUNCTUATION.includes(lastCharacter)) {
			trimmed = trimmed.slice(0, -1);
			continue;
		}
		/* istanbul ignore next -- 直前で ')' 終わりを確かめているため ')' 側の match は必ず1件以上返る */
		const isUnbalancedClose =
			lastCharacter === ')' &&
			(trimmed.match(/\(/g) || []).length < (trimmed.match(/\)/g) || []).length;
		if (isUnbalancedClose) {
			trimmed = trimmed.slice(0, -1);
			continue;
		}
		break;
	}
	return trimmed;
}

/**
 * テキストを「通常文字列」と「URL」のセグメントに分割する。
 *
 * @param {string} text 分割対象のテキスト
 * @returns {Array<{type: 'text'|'url', value: string}>} 元のテキストを復元できる並び
 */
export function splitTextByUrl(text) {
	if (!text) return [];

	const segments = [];
	let lastIndex = 0;

	// exec を繰り返すため lastIndex を持つ正規表現を都度生成する
	const pattern = new RegExp(URL_PATTERN.source, 'g');
	let match = pattern.exec(text);
	while (match !== null) {
		const url = trimTrailingPunctuation(match[0]);
		/* istanbul ignore else -- URL は http 始まりなので末尾記号を落としても空にならない */
		if (url) {
			if (match.index > lastIndex) {
				segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
			}
			segments.push({ type: 'url', value: url });
			lastIndex = match.index + url.length;
		}
		match = pattern.exec(text);
	}

	if (lastIndex < text.length) {
		segments.push({ type: 'text', value: text.slice(lastIndex) });
	}
	return segments;
}

/**
 * テキストにリンク化できるURLが含まれるか。
 */
export function hasUrl(text) {
	return splitTextByUrl(text).some((segment) => segment.type === 'url');
}
